import React, { useState, useEffect, useMemo } from 'react'
import {
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
} from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'
import { Button, Tooltip } from '~/components/_atoms'
import { Mute, Unmute, Video, VideoOff } from '~/components/icons/YnsIcon'
import { useTranslation } from '~/hooks/useTranslation'
import { useCoachChat } from './CoachChatContext'
import parse, { domToReact } from 'html-react-parser'

const parseWithNewTabLinks = (html) =>
  parse(html.replace(/\n/g, '<br>'), {
    replace(domNode) {
      if (domNode.type === 'tag' && domNode.name === 'a') {
        return (
          <a {...domNode.attribs} target="_blank" rel="noopener noreferrer">
            {domToReact(domNode.children)}
          </a>
        )
      }
    },
  })

const CandidateInfo = ({
  chatToken,
  showEndChatButton = true,
  /** EasyRTC id for this UI slot — required to end the correct chat when multiple are active */
  slotCandidateEasyrtcid = null,
  /** text | audio | video — controls which media buttons are shown (CTS-9777) */
  chatMode = 'text',
}) => {
  const { chatManager, isDarkMode, connectionState, activeChats, endChat } =
    useCoachChat()
  const { t } = useTranslation('coach_chat')

  const showPauseControls = chatMode === 'video'
  const showMuteControls = chatMode === 'audio' || chatMode === 'video'
  const isPaused = connectionState?.videoPaused || false
  const isMuted = connectionState?.audioMuted || false

  const muteLabel = isMuted
    ? t('career_development.unmute_microphone', {
        defaultValue: 'Unmute microphone',
      })
    : t('career_development.mute_microphone', {
        defaultValue: 'Mute microphone',
      })

  const pauseLabel = isPaused
    ? t('career_development.resume_chat', { defaultValue: 'Resume Chat' })
    : t('career_development.pause_chat', { defaultValue: 'Pause Chat' })
  const [candidate, setCandidate] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [browserInfo, setBrowserInfo] = useState(null)

  useEffect(() => {
    if (!chatToken || !chatManager) {
      setLoading(false)
      return
    }

    // Get browser info from room API fields (similar to legacy chat.js)
    const getBrowserInfo = () => {
      const easyrtc = chatManager?.rtcService?.getEasyRTC()
      if (!easyrtc || !connectionState?.chatRoom) {
        return null
      }

      // Find the easyrtcid for this chatToken by searching room occupants
      const roomOccupants = easyrtc.getRoomOccupantsAsMap(
        connectionState.chatRoom,
      )
      if (!roomOccupants) {
        return null
      }

      // Find the occupant with matching username (chatToken)
      let candidateEasyrtcid = null
      for (const easyrtcid in roomOccupants) {
        const occupant = roomOccupants[easyrtcid]
        if (occupant.username === chatToken) {
          candidateEasyrtcid = easyrtcid
          break
        }
      }

      if (!candidateEasyrtcid) {
        return null
      }

      // Get browser and isMobile from room API fields
      const browser = easyrtc.getRoomApiField(
        connectionState.chatRoom,
        candidateEasyrtcid,
        'browser',
      )
      const isMobile = easyrtc.getRoomApiField(
        connectionState.chatRoom,
        candidateEasyrtcid,
        'isMobile',
      )

      if (browser) {
        return `${browser}${isMobile ? ' (Mobile)' : ''}`
      }
      return null
    }

    // Check if candidate info is already cached
    if (chatManager.candidateInfo && chatManager.candidateInfo[chatToken]) {
      setCandidate(chatManager.candidateInfo[chatToken])
      const browser = getBrowserInfo()
      setBrowserInfo(browser)
      setLoading(false)
      return
    }

    // Listen for candidate info fetched event
    const handleCandidateInfoFetched = (data) => {
      if (data.chatToken === chatToken) {
        setCandidate(data.candidate)
        const browser = getBrowserInfo()
        setBrowserInfo(browser)
        setLoading(false)
      }
    }

    chatManager.addEventListener(
      'candidateInfoFetched',
      handleCandidateInfoFetched,
    )

    // Fetch candidate info
    chatManager
      .fetchCandidateInfo(chatToken)
      .then((candidateData) => {
        if (candidateData) {
          setCandidate(candidateData)
          const browser = getBrowserInfo()
          setBrowserInfo(browser)
        } else {
          setError('Candidate info not available')
        }
        setLoading(false)
        return candidateData
      })
      .catch((err) => {
        console.error('Error fetching candidate info:', err)
        setError('Failed to load candidate info')
        setLoading(false)
        throw err
      })

    return () => {
      chatManager.removeEventListener(
        'candidateInfoFetched',
        handleCandidateInfoFetched,
      )
    }
  }, [chatToken, chatManager, connectionState?.chatRoom])

  // Find the active chat that matches this chatToken (reactive with useMemo)
  // Must be called before early returns to satisfy React Hooks rules
  const activeChat = useMemo(() => {
    if (!chatToken || !activeChats || !chatManager) return null

    const easyrtc = chatManager?.rtcService?.getEasyRTC()
    if (!easyrtc || !connectionState?.chatRoom) return null

    // Find the active chat where the candidateToken matches
    return activeChats.find((chat) => {
      const candidateEasyrtcid =
        chat.candidateEasyrtcid || chat.candidateId || chat.easyrtcid
      if (!candidateEasyrtcid) return false

      // Get candidate token from room API field
      const candidateTokenFromRoom = easyrtc.getRoomApiField(
        connectionState.chatRoom,
        candidateEasyrtcid,
        'chatToken',
      )

      return candidateTokenFromRoom === chatToken
    })
  }, [chatToken, activeChats, chatManager, connectionState])

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-gray-500">
        Loading candidate info...
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-red-600">
        {error}
      </div>
    )
  }

  if (!candidate) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-gray-500">
        Candidate info not available
      </div>
    )
  }

  const formatSocialMediaUrl = (url) => {
    if (!url) return ''
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url
    }
    return `http://${url}`
  }

  const renderFullInfoTable = () => {
    const tableClasses = `w-full border-collapse mb-4 ${
      isDarkMode ? 'text-gray-100' : ''
    }`
    const tdClasses = `p-2 border align-top ${
      isDarkMode ? 'border-gray-600 text-gray-100' : 'border-gray-200'
    }`
    const tdFirstChildClasses = `w-1/3 font-medium ${
      isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-50'
    }`
    const linkClasses = isDarkMode
      ? 'text-blue-400 no-underline hover:underline'
      : 'text-blue-600 no-underline hover:underline'

    const specialInstructionsClasses = `mt-4 p-4 rounded-lg border ${
      isDarkMode
        ? 'bg-amber-900 border-amber-800'
        : 'bg-yellow-100 border-yellow-500'
    }`
    const specialInstructionsHeaderClasses = `font-semibold mb-2 ${
      isDarkMode ? 'text-yellow-400' : 'text-amber-800'
    }`
    const specialInstructionsContentClasses = `whitespace-pre-wrap ${
      isDarkMode ? 'text-yellow-100' : 'text-amber-900'
    }`

    return (
      <>
        <table className={tableClasses}>
          <tbody>
            {candidate.name && (
              <tr>
                <td className={`${tdClasses} ${tdFirstChildClasses}`}>Name</td>
                <td className={tdClasses}>
                  {candidate.candidate_path ? (
                    <a
                      href={candidate.candidate_path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={linkClasses}
                    >
                      {candidate.name}
                    </a>
                  ) : (
                    candidate.name
                  )}
                </td>
              </tr>
            )}
            {candidate.email && (
              <tr>
                <td className={`${tdClasses} ${tdFirstChildClasses}`}>Email</td>
                <td className={tdClasses}>
                  <a
                    href={`mailto:${candidate.email}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={linkClasses}
                  >
                    {candidate.email}
                  </a>
                </td>
              </tr>
            )}
            {candidate.company && (
              <tr>
                <td className={`${tdClasses} ${tdFirstChildClasses}`}>
                  Company
                </td>
                <td className={tdClasses}>{candidate.company}</td>
              </tr>
            )}
            {candidate.product && (
              <tr>
                <td className={`${tdClasses} ${tdFirstChildClasses}`}>
                  Product
                </td>
                <td className={tdClasses}>{candidate.product}</td>
              </tr>
            )}
            {candidate.program_level && (
              <tr>
                <td className={`${tdClasses} ${tdFirstChildClasses}`}>
                  Program Level
                </td>
                <td className={tdClasses}>{candidate.program_level}</td>
              </tr>
            )}
            {candidate.op_next_gen !== undefined && (
              <tr>
                <td className={`${tdClasses} ${tdFirstChildClasses}`}>
                  OP Next-Gen Enabled
                </td>
                <td className={tdClasses}>{candidate.op_next_gen}</td>
              </tr>
            )}
            {candidate.interests && (
              <tr>
                <td className={`${tdClasses} ${tdFirstChildClasses}`}>
                  Top Interests
                </td>
                <td className={tdClasses}>{candidate.interests}</td>
              </tr>
            )}
            {candidate.job_title && (
              <tr>
                <td className={`${tdClasses} ${tdFirstChildClasses}`}>
                  Desired Job Title
                </td>
                <td className={tdClasses}>{candidate.job_title}</td>
              </tr>
            )}
            {candidate.industry && (
              <tr>
                <td className={`${tdClasses} ${tdFirstChildClasses}`}>
                  Desired Industry
                </td>
                <td className={tdClasses}>{candidate.industry}</td>
              </tr>
            )}
            {candidate.location && (
              <tr>
                <td className={`${tdClasses} ${tdFirstChildClasses}`}>
                  Desired Location
                </td>
                <td className={tdClasses}>{candidate.location}</td>
              </tr>
            )}
            {candidate.career_level && (
              <tr>
                <td className={`${tdClasses} ${tdFirstChildClasses}`}>
                  Career Level
                </td>
                <td className={tdClasses}>{candidate.career_level}</td>
              </tr>
            )}
            {candidate.social_media_urls &&
              Array.isArray(candidate.social_media_urls) &&
              candidate.social_media_urls.length > 0 && (
                <tr>
                  <td className={`${tdClasses} ${tdFirstChildClasses}`}>
                    Social Media Profiles
                  </td>
                  <td className={tdClasses}>
                    {candidate.social_media_urls.map((url, index) => (
                      <React.Fragment key={index}>
                        <a
                          href={formatSocialMediaUrl(url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={linkClasses}
                        >
                          {url}
                        </a>
                        {index < candidate.social_media_urls.length - 1 && (
                          <br />
                        )}
                      </React.Fragment>
                    ))}
                  </td>
                </tr>
              )}
            {browserInfo && (
              <tr>
                <td className={`${tdClasses} ${tdFirstChildClasses}`}>
                  Browser
                </td>
                <td className={tdClasses}>{browserInfo}</td>
              </tr>
            )}
          </tbody>
        </table>

        {candidate.special_instructions && (
          <div className={specialInstructionsClasses}>
            <div className={specialInstructionsHeaderClasses}>
              Special Instructions:
            </div>
            <div className={specialInstructionsContentClasses}>
              {parseWithNewTabLinks(candidate.special_instructions)}
            </div>
          </div>
        )}
      </>
    )
  }

  const handleEndChat = () => {
    // Show confirmation dialog (like legacy chat.js)
    if (
      !window.confirm(
        t('candidate_info.sure_end', 'Are you sure you want to end this chat?'),
      )
    ) {
      return
    }

    // Try to find the active chat
    let chatId = null

    if (slotCandidateEasyrtcid && activeChats?.length) {
      const chatForSlot = activeChats.find(
        (chat) =>
          chat.candidateEasyrtcid === slotCandidateEasyrtcid ||
          chat.candidateId === slotCandidateEasyrtcid ||
          chat.easyrtcid === slotCandidateEasyrtcid,
      )
      if (chatForSlot) {
        chatId = chatForSlot.id || chatForSlot.candidateEasyrtcid
      }
    }

    if (!chatId && activeChat) {
      chatId = activeChat.id || activeChat.candidateEasyrtcid
    } else if (!chatId && activeChats && activeChats.length > 0) {
      // Fallback: try to find chat by matching chatToken
      const easyrtc = chatManager?.rtcService?.getEasyRTC()
      if (easyrtc && connectionState?.chatRoom) {
        for (const chat of activeChats) {
          const candidateEasyrtcid =
            chat.candidateEasyrtcid || chat.candidateId || chat.easyrtcid
          if (candidateEasyrtcid) {
            const candidateTokenFromRoom = easyrtc.getRoomApiField(
              connectionState.chatRoom,
              candidateEasyrtcid,
              'chatToken',
            )
            if (candidateTokenFromRoom === chatToken) {
              chatId = chat.id || candidateEasyrtcid
              break
            }
          }
        }
      }
      // Only default to the sole active chat — never pick slot 1 when two chats exist
      if (!chatId && activeChats.length === 1) {
        chatId = activeChats[0].id || activeChats[0].candidateEasyrtcid
      }
    }

    if (chatId) {
      endChat(chatId)
    } else {
      console.warn('CandidateInfo - Could not determine chat ID to end', {
        activeChat,
        activeChats,
        chatToken,
        slotCandidateEasyrtcid,
      })
    }
  }

  const textColor = isDarkMode ? '#f3f4f4' : '#111827'
  const chipColor = isDarkMode ? '#4b5563' : '#e5e7eb'
  const chipTextColor = isDarkMode ? '#e5e7eb' : '#374151'
  const textColorClass = isDarkMode ? 'text-gray-100' : 'text-gray-900'

  return (
    <>
      <div className={`flex flex-col px-4 py-3 ${textColorClass}`}>
        <div className="flex flex-col gap-1">
          {candidate.name && (
            <div className="flex flex-wrap items-center gap-2">
              <h2 className={`m-0 text-2xl font-semibold ${textColorClass}`}>
                {candidate.candidate_path ? (
                  <a
                    href={candidate.candidate_path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-inherit no-underline"
                  >
                    {candidate.name}
                  </a>
                ) : (
                  candidate.name
                )}
              </h2>
              {candidate.product && (
                <Chip
                  label={candidate.product}
                  size="small"
                  sx={{
                    backgroundColor: chipColor,
                    color: chipTextColor,
                    fontSize: '0.75rem',
                    height: '24px',
                  }}
                />
              )}
              {candidate.program_level && (
                <Chip
                  label={candidate.program_level}
                  size="small"
                  sx={{
                    backgroundColor: chipColor,
                    color: chipTextColor,
                    fontSize: '0.75rem',
                    height: '24px',
                  }}
                />
              )}
            </div>
          )}
          {candidate.email && (
            <div className={`text-sm ${textColorClass}`}>
              <a
                href={`mailto:${candidate.email}`}
                target="_blank"
                rel="noopener noreferrer"
                className={`${
                  isDarkMode ? 'text-blue-400' : 'text-blue-600'
                } no-underline hover:underline`}
              >
                {candidate.email}
              </a>
            </div>
          )}
          {candidate.company && (
            <div className={`text-sm ${textColorClass}`}>
              {candidate.company}
            </div>
          )}
          <div className="mt-2 flex items-center justify-between gap-2">
            <Button
              component="button"
              type="button"
              variant="outline"
              color="neutral"
              size="sm"
              onClick={() => setModalOpen(true)}
              className={
                isDarkMode
                  ? 'border-gray-500 bg-transparent text-gray-100 hover:bg-gray-700'
                  : ''
              }
            >
              {t('candidate_info.see_more')}
            </Button>
            <div className="flex items-center gap-2">
              {/* CTS-9777: pause/mute live beside End Chat (round icon buttons) */}
              {showPauseControls && (
                <Tooltip content={pauseLabel} placement="top">
                  <Button
                    component="button"
                    type="button"
                    id={isPaused ? 'btn_unpause_chat' : 'btn_pause_chat'}
                    variant="outline"
                    color="neutral"
                    size="icon"
                    aria-label={pauseLabel}
                    aria-pressed={isPaused}
                    onClick={() => {
                      if (isPaused) {
                        chatManager?.unpauseChat()
                      } else {
                        chatManager?.pauseChat()
                      }
                    }}
                    className={
                      isDarkMode
                        ? 'border-gray-500 bg-transparent text-gray-100 hover:bg-gray-700'
                        : ''
                    }
                  >
                    {isPaused ? (
                      <Video className="h-5 w-5" />
                    ) : (
                      <VideoOff className="h-5 w-5" />
                    )}
                  </Button>
                </Tooltip>
              )}
              {showMuteControls && (
                <Tooltip content={muteLabel} placement="top">
                  <Button
                    component="button"
                    type="button"
                    id={isMuted ? 'btn_unmute_chat' : 'btn_mute_chat'}
                    variant="outline"
                    color="neutral"
                    size="icon"
                    aria-label={muteLabel}
                    aria-pressed={isMuted}
                    onClick={() => {
                      if (isMuted) {
                        chatManager?.unmuteChat()
                      } else {
                        chatManager?.muteChat()
                      }
                    }}
                    className={
                      isDarkMode
                        ? 'border-gray-500 bg-transparent text-gray-100 hover:bg-gray-700'
                        : ''
                    }
                  >
                    {isMuted ? (
                      <Unmute className="h-5 w-5" />
                    ) : (
                      <Mute className="h-5 w-5" />
                    )}
                  </Button>
                </Tooltip>
              )}
              {showEndChatButton && (
                <Button
                  component="button"
                  type="button"
                  variant="outline"
                  color="destructive"
                  size="sm"
                  onClick={handleEndChat}
                  className={
                    isDarkMode
                      ? 'border-red-400 bg-transparent text-red-300 hover:bg-red-900/30'
                      : ''
                  }
                >
                  {t('candidate_info.end_chat', 'End Chat')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
            color: textColor,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
            pb: 2,
          }}
        >
          <Box component="span" sx={{ fontWeight: 600, fontSize: '1.25rem' }}>
            {candidate.name || 'Candidate Information'}
          </Box>
          <IconButton
            onClick={() => setModalOpen(false)}
            sx={{
              color: textColor,
              '&:hover': {
                backgroundColor: isDarkMode
                  ? 'rgba(255, 255, 255, 0.1)'
                  : 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            pt: 3,
            color: textColor,
          }}
        >
          {renderFullInfoTable()}
        </DialogContent>
      </Dialog>
    </>
  )
}

export default CandidateInfo
